import React, { useState, useEffect } from 'react';
import { ShoppingBag, Plus, Edit2, Trash2, Save, X, Star, Clock, Flame, Leaf, Eye, EyeOff, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ProductsManagement = () => {
  const { user, restaurants: userRestaurants } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    restaurantId: '',
    categoryId: '',
    name: '',
    nameEn: '',
    description: '',
    price: '',
    discountedPrice: '',
    image: '',
    imagePreview: '',
    originalFileName: '',
    selectedFile: null, // Seçilen dosya için
    ingredients: '',
    calories: '',
    preparationTime: '',
    isVegetarian: false,
    isVegan: false,
    isGlutenFree: false,
    isSpicy: false,
    spicyLevel: 0,
    isAvailable: true,
    isFeatured: false,
    displayOrder: 0
  });

  // Load data from API
  useEffect(() => {
    const initializeRestaurants = async () => {
      if (userRestaurants && Array.isArray(userRestaurants) && userRestaurants.length > 0) {
        setRestaurants(userRestaurants);
        
        // Eğer sadece 1 restaurant varsa, onu otomatik seç
        if (userRestaurants.length === 1) {
          const restaurantId = getRestaurantId(userRestaurants[0]);
          if (restaurantId) {
            setFormData(prev => ({
              ...prev,
              restaurantId: restaurantId
            }));
            console.log('Otomatik restoran seçildi (userRestaurants):', restaurantId);
          }
        }
      } else {
        // userRestaurants yoksa veya boşsa API'den çek
        await fetchRestaurants();
      }
    };
    
    initializeRestaurants();
    fetchProducts();
    fetchCategories();
  }, [userRestaurants]);

  const fetchProducts = async () => {
    console.log('=== fetchProducts başladı ===');
    console.log('User:', user);
    
    if (!user) {
      console.log('User yok, fonksiyon sonlandı');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      console.log('Token alındı:', token ? 'Token var' : 'Token yok');
      
      let url = `${API_BASE_URL}/api/Products`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      console.log('UserRole:', userRole);
      console.log('User ID:', user.id);
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Products/user/${user.id}`;
      }

      console.log('Fetch URL:', url);
      console.log('Fetch headers:', {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response alındı:', response);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Products data:', data);
        setProducts(data);
        console.log('Products state güncellendi');
      } else {
        console.log('Response başarısız');
        const errorText = await response.text();
        console.log('Error text:', errorText);
        setError('Ürünler yüklenirken hata oluştu');
      }
    } catch (err) {
      console.log('FetchProducts catch bloğu:', err);
      console.log('Error message:', err.message);
      setError('API bağlantı hatası');
      console.error('API Error:', err);
    } finally {
      console.log('FetchProducts finally bloğu');
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('authToken');
      let url = `${API_BASE_URL}/api/Restaurants`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Restaurants/user/${user.id}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRestaurants(data);
        
        // Eğer sadece 1 restaurant varsa, onu otomatik seç
        if (Array.isArray(data) && data.length === 1) {
          const restaurantId = getRestaurantId(data[0]);
          if (restaurantId) {
            setFormData(prev => ({
              ...prev,
              restaurantId: restaurantId
            }));
            console.log('Otomatik restoran seçildi (fetchRestaurants):', restaurantId);
          }
        }
      }
    } catch (err) {
      console.error('Restaurant API Error:', err);
    }
  };

  const fetchCategories = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('authToken');
      let url = `${API_BASE_URL}/api/Categories`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Categories/user/${user.id}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Categories API Error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // If restaurant changes, reset category selection
    if (name === 'restaurantId') {
      setFormData(prev => ({
        ...prev,
        categoryId: '' // Reset category when restaurant changes
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Basit dosya adı oluştur
      const originalName = file.name;
      const extension = originalName.split('.').pop().toLowerCase();
      const baseName = originalName.split('.')[0].toLowerCase().replace(/[^a-z0-9]/g, '-');
      
      // Basit dosya adı (timestamp olmadan)
      const fileName = `${baseName}.${extension}`;
      const imagePath = `/MenuResim/${fileName}`;
      
      // Resim önizlemesi için URL oluştur
      const imageUrl = URL.createObjectURL(file);
      
      // Tek seferde tüm değerleri güncelle
      setFormData(prev => ({
        ...prev,
        image: imagePath,
        originalFileName: originalName,
        imagePreview: imageUrl,
        selectedFile: file // Dosyayı sakla
      }));
    }
  };

  const resetForm = () => {
    // Eğer tek restoran varsa, onu otomatik seç
    // Önce restaurants state'ini kontrol et, yoksa userRestaurants'ı kontrol et
    const availableRestaurants = (restaurants && Array.isArray(restaurants) && restaurants.length > 0) 
      ? restaurants 
      : (userRestaurants && Array.isArray(userRestaurants) && userRestaurants.length > 0) 
        ? userRestaurants 
        : [];
    
    let defaultRestaurantId = '';
    if (availableRestaurants.length === 1) {
      const restaurantId = getRestaurantId(availableRestaurants[0]);
      if (restaurantId) {
        defaultRestaurantId = String(restaurantId);
        console.log('resetForm - Otomatik restoran seçildi:', defaultRestaurantId);
      }
    }
    
    setFormData({
      id: '', // ID alanını da ekle
      restaurantId: defaultRestaurantId,
      categoryId: '',
      name: '',
      nameEn: '',
      description: '',
      price: '',
      discountedPrice: '',
      image: '',
      imagePreview: '',
      originalFileName: '',
      selectedFile: null,
      ingredients: '',
      calories: '',
      preparationTime: '',
      isVegetarian: false,
      isVegan: false,
      isGlutenFree: false,
      isSpicy: false,
      spicyLevel: 0,
      isAvailable: true,
      isFeatured: false,
      displayOrder: 0
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleEdit = (product) => {
    // Try different possible ID field names
    const possibleIds = [
      product.productId,
      product.id, 
      product.ProductId,
      product.ID
    ];
    
    // Find the first non-null ID
    const actualId = possibleIds.find(id => id != null);
    
    if (!actualId) {
      setError('Product ID bulunamadı');
      return;
    }
    
    setError('');
    setIsCreating(false);
    setEditingProduct(actualId);
    
    const resolvedImagePreview = getProductImageUrl(product.image) || '';

    setFormData({
      id: actualId, // ID'yi formData'ya ekle
      restaurantId: product.restaurantId || '',
      categoryId: product.categoryId || '',
      name: product.name || '',
      nameEn: product.englishName || product.EnglishName || '',
      description: product.description || '',
      price: product.price || '',
      discountedPrice: product.discountedPrice || '',
      image: product.image || '',
      imagePreview: resolvedImagePreview,
      originalFileName: product.image ? product.image.split('/').pop() : '',
      ingredients: product.ingredients || '',
      calories: product.calories || '',
      preparationTime: product.preparationTime || '',
      isVegetarian: product.isVegetarian || false,
      isVegan: product.isVegan || false,
      isGlutenFree: product.isGlutenFree || false,
      isSpicy: product.isSpicy || false,
      spicyLevel: product.spicyLevel || 0,
      isAvailable: product.isAvailable !== undefined ? product.isAvailable : true,
      isFeatured: product.isFeatured || false,
      displayOrder: product.displayOrder || 0
    });

  };

  const handleSave = async () => {
    console.log('=== handleSave başladı ===');
    console.log('FormData:', formData);
    console.log('EditingProduct:', editingProduct);
    console.log('IsCreating:', isCreating);
    
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.name.trim()) {
        console.log('Validation hatası: Ürün adı zorunludur');
        setError('Ürün adı zorunludur');
        return;
      }

      if (!formData.restaurantId) {
        console.log('Validation hatası: Restoran seçimi zorunludur');
        setError('Restoran seçimi zorunludur');
        return;
      }

      if (!formData.categoryId) {
        console.log('Validation hatası: Kategori seçimi zorunludur');
        setError('Kategori seçimi zorunludur');
        return;
      }

      // Fiyat validasyonu - string'e çevir ve kontrol et
      const priceString = formData.price.toString();
      const normalizedPriceForValidation = priceString.replace(',', '.');
      if (!formData.price || parseFloat(normalizedPriceForValidation) <= 0) {
        console.log('Validation hatası: Geçerli bir fiyat girin');
        setError('Geçerli bir fiyat girin');
        return;
      }

      // FormData oluştur (dosya yükleme için)
      const formDataToSend = new FormData();
      
      // Temel verileri ekle - Sadece DTO'daki alanlar
      if (editingProduct) {
        formDataToSend.append('id', formData.id);
      }
      formDataToSend.append('restaurantId', formData.restaurantId.toString());
      formDataToSend.append('categoryId', formData.categoryId.toString());
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('englishName', (formData.nameEn || '').trim());
      formDataToSend.append('description', formData.description.trim() || '');
      // Fiyatları decimal'e dönüştür ve gönder
      console.log('=== FİYAT DEBUG ===');
      console.log('Orijinal fiyat:', formData.price);
      console.log('Orijinal indirimli fiyat:', formData.discountedPrice);
      
      // Virgülü nokta ile değiştir ve decimal'e dönüştür
      const normalizedPrice = formData.price.toString().replace(',', '.');
      const normalizedDiscountedPrice = formData.discountedPrice ? formData.discountedPrice.toString().replace(',', '.') : '';
      
      const priceDecimal = parseFloat(normalizedPrice);
      const discountedPriceDecimal = formData.discountedPrice ? parseFloat(normalizedDiscountedPrice) : null;
      
      console.log('Normalize edilmiş fiyat:', normalizedPrice);
      console.log('Decimal fiyat:', priceDecimal);
      
      // Direkt decimal değerleri gönder
      formDataToSend.append('Price', priceDecimal.toString());
      if (formData.discountedPrice) {
        formDataToSend.append('DiscountedPrice', discountedPriceDecimal.toString());
      }
      formDataToSend.append('image', formData.image.trim() || '');
      formDataToSend.append('isAvailable', formData.isAvailable.toString());
      formDataToSend.append('displayOrder', (parseInt(formData.displayOrder) || 0).toString());
      
      // Dosya varsa ekle
      if (formData.selectedFile) {
        formDataToSend.append('imageFile', formData.selectedFile);
      }

      console.log('FormData hazırlandı:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      let response;
      
      const token = localStorage.getItem('authToken');
      console.log('Token alındı:', token ? 'Token var' : 'Token yok');
      
      // Test endpoint'ini dene
      console.log('Test endpoint deneniyor...');
      const testResponse = await fetch(`${API_BASE_URL}/api/Products/test-form`, {
        method: 'POST',
        body: formDataToSend
      });
      console.log('Test response:', testResponse.status, await testResponse.text());
      
      if (editingProduct) {
        // Update existing product
        console.log('UPDATE işlemi başlıyor...');
        console.log('URL:', `${API_BASE_URL}/api/Products/${editingProduct}`);
        console.log('Method: PUT');
        console.log('Headers:', {
          'Authorization': `Bearer ${token}`,
        });
        console.log('Body: FormData (multipart/form-data)');
        
        response = await fetch(`${API_BASE_URL}/api/Products/${editingProduct}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
          },
          body: formDataToSend
        });
      } else {
        // Create new product
        console.log('CREATE işlemi başlıyor...');
        console.log('URL:', `${API_BASE_URL}/api/Products`);
        console.log('Method: POST');
        console.log('Headers:', {
          'Authorization': `Bearer ${token}`,
        });
        console.log('Body: FormData (multipart/form-data)');
        
        response = await fetch(`${API_BASE_URL}/api/Products`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
          },
          body: formDataToSend
        });
      }

      console.log('Response alındı:', response);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        console.log('Response başarılı!');
        const responseData = await response.text();
        console.log('Response data:', responseData);

        await fetchProducts();
        setIsCreating(false);
        setEditingProduct(null);
        resetForm();
        console.log('İşlem tamamlandı');
      } else if (response.status === 500) {
        console.log('500 hatası alındı');
        const errorText = await response.text();
        console.log('500 hata detayı:', errorText);
        
        if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
          console.log('Database trigger hatası, özel işlem yapılıyor');
          setError('');
          setTimeout(async () => {
            await fetchProducts();
            if (!editingProduct) {
              const savedProduct = products.find(p => 
                p.name === formData.name && 
                p.restaurantId === formData.restaurantId
              );
              if (savedProduct) {
                setIsCreating(false);
                resetForm();
              } else {
                setTimeout(async () => {
                  await fetchProducts();
                  const laterCheck = products.find(p => 
                    p.name === formData.name && 
                    p.restaurantId === formData.restaurantId
                  );
                  if (laterCheck) {
                    setIsCreating(false);
                    resetForm();
                  } else {
                    setError('Ürün kaydedilemedi, lütfen tekrar deneyin.');
                  }
                }, 1500);
              }
            } else {
              setEditingProduct(null);
              resetForm();
            }
          }, 1000);
        } else {
          console.log('500 hatası ama trigger hatası değil');
          setError(`Server Hatası (500): ${errorText}`);
        }
      } else {
        console.log('Diğer hata alındı:', response.status);
        const errorText = await response.text();
        console.log('Hata detayı:', errorText);
        setError(`API Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
      }
    } catch (err) {
      console.log('Catch bloğuna düştü:', err);
      console.log('Error message:', err.message);
      console.log('Error stack:', err.stack);
      setError(`Bağlantı hatası: ${err.message}`);
    } finally {
      console.log('Finally bloğu çalıştı');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingProduct(null);
    resetForm();
    setError('');
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      try {
        const productToDelete = products.find(p => {
          const possibleIds = [p.productId, p.id, p.ProductId, p.ID];
          return possibleIds.includes(productId);
        });
        
        if (!productToDelete) {
          setError('Product bulunamadı');
          return;
        }
        
        const actualId = [productToDelete.productId, productToDelete.id, productToDelete.ProductId, productToDelete.ID]
          .find(id => id != null);
        
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/Products/${actualId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          await fetchProducts();
        } else if (response.status === 500) {
          const errorText = await response.text();
          if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
            setTimeout(async () => {
              await fetchProducts();
              setError('');
            }, 1000);
          } else if (errorText.includes('REFERENCE constraint') || errorText.includes('FK_')) {
            setError('Bu ürün silinemez çünkü bağlı kayıtları var.');
          } else {
            setError(`Server Hatası (500): ${errorText}`);
          }
        } else {
          const errorText = await response.text();
          setError(`Silme Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
        }
      } catch (err) {
        setError(`Bağlantı hatası: ${err.message}`);
      }
    }
  };

  const toggleProductStatus = async (product) => {
    try {
      console.log('toggleProductStatus çağrıldı:', product);
      const possibleIds = [product.productId, product.id, product.ProductId, product.ID];
      const actualId = possibleIds.find(id => id != null);
      
      console.log('Product ID:', actualId);
      
      if (!actualId) {
        console.error('Product ID bulunamadı');
        setError('Product ID bulunamadı');
        return;
      }

      // Validasyon: Gerekli alanları kontrol et
      if (!product.name || !product.name.trim()) {
        console.error('Product name bulunamadı:', product);
        setError('Ürün adı bulunamadı');
        return;
      }

      if (!product.restaurantId) {
        console.error('Restaurant ID bulunamadı:', product);
        setError('Restoran ID bulunamadı');
        return;
      }

      if (!product.categoryId) {
        console.error('Category ID bulunamadı:', product);
        setError('Kategori ID bulunamadı');
        return;
      }

      // API FormData bekliyor (handleSave'deki formatı kullan)
      const formDataToSend = new FormData();
      formDataToSend.append('id', actualId);
      formDataToSend.append('restaurantId', product.restaurantId.toString());
      formDataToSend.append('categoryId', product.categoryId.toString());
      
      // Name field'ını kontrol et ve ekle
      const productName = (product.name || product.Name || '').trim();
      if (!productName) {
        console.error('Product name boş veya undefined:', product);
        setError('Ürün adı bulunamadı veya boş');
        return;
      }
      formDataToSend.append('name', productName);
      formDataToSend.append('englishName', (product.englishName || product.EnglishName || '').trim());
      
      formDataToSend.append('description', (product.description || product.Description || '').trim() || '');
      
      // Fiyatları decimal formatında gönder (handleSave'deki gibi)
      const productPrice = product.price || product.Price || 0;
      const normalizedPrice = productPrice.toString().replace(',', '.');
      const priceDecimal = parseFloat(normalizedPrice);
      formDataToSend.append('Price', priceDecimal.toString());
      
      if (product.discountedPrice || product.DiscountedPrice) {
        const discountedPrice = product.discountedPrice || product.DiscountedPrice;
        const normalizedDiscountedPrice = discountedPrice.toString().replace(',', '.');
        const discountedPriceDecimal = parseFloat(normalizedDiscountedPrice);
        formDataToSend.append('DiscountedPrice', discountedPriceDecimal.toString());
      }
      
      if (product.image || product.Image) {
        formDataToSend.append('image', (product.image || product.Image || '').trim());
      }
      
      formDataToSend.append('isAvailable', (!product.isAvailable).toString());
      formDataToSend.append('displayOrder', (product.displayOrder || product.DisplayOrder || 0).toString());

      console.log('FormData hazırlandı:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }
      console.log('New isAvailable:', !product.isAvailable);

      const token = localStorage.getItem('authToken');
      console.log('Token:', token ? 'Var' : 'Yok');
      
      const response = await fetch(`${API_BASE_URL}/api/Products/${actualId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
        },
        body: formDataToSend
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        console.log('Başarılı, ürünler yeniden yükleniyor...');
        await fetchProducts();
        setError('');
      } else if (response.status === 500) {
        const errorText = await response.text();
        console.error('500 hatası:', errorText);
        if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
          setTimeout(async () => {
            await fetchProducts();
            setError('');
          }, 1000);
        } else {
          setError(`Durum güncellenirken hata oluştu: ${errorText}`);
        }
      } else {
        const errorText = await response.text();
        console.error('Hata:', response.status, errorText);
        setError(`Durum güncellenirken hata oluştu: ${errorText}`);
      }
    } catch (err) {
      console.error('Toggle error:', err);
      setError(`API bağlantı hatası: ${err.message}`);
    }
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => {
      const possibleIds = [r.restaurantId, r.id, r.RestaurantId, r.ID];
      return possibleIds.includes(restaurantId);
    });
    return restaurant ? restaurant.name : restaurantId;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => {
      const possibleIds = [c.categoryId, c.id, c.CategoryId, c.ID];
      return possibleIds.includes(categoryId);
    });
    return category ? category.name : categoryId;
  };

  const getRestaurantId = (restaurant) => {
    const possibleIds = [restaurant.restaurantId, restaurant.id, restaurant.RestaurantId, restaurant.ID];
    return possibleIds.find(id => id != null);
  };

  const getCategoryId = (category) => {
    const possibleIds = [category.categoryId, category.id, category.CategoryId, category.ID];
    return possibleIds.find(id => id != null);
  };

  const getProductImageUrl = (imagePath) => {
    if (!imagePath) return null;
    const raw = String(imagePath).trim();
    if (!raw) return null;
    const absolute = raw.startsWith('http://') || raw.startsWith('https://');
    const merged = absolute ? raw : `${API_BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
    return encodeURI(merged);
  };

  // Filter categories based on selected restaurant
  const getFilteredCategories = () => {
    if (!formData.restaurantId) return [];
    
    return categories.filter(category => {
      return category.restaurantId === formData.restaurantId;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter products based on search term
  const getFilteredProducts = () => {
    if (!searchTerm.trim()) {
      return products;
    }
    
    return products.filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingBag className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ürün Yönetimi</h1>
                <p className="text-gray-600">Menü ürünlerini ekleyin ve düzenleyin</p>
              </div>
            </div>
            {!isCreating && !editingProduct && (
              <button
                onClick={handleCreate}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Yeni Ürün</span>
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {(isCreating || editingProduct) && (
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-900">Temel Bilgiler</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Restoran *
                  </label>
                  <select
                    name="restaurantId"
                    value={formData.restaurantId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Restoran seçiniz</option>
                    {restaurants.map((restaurant) => {
                      const restaurantId = getRestaurantId(restaurant);
                      return (
                        <option key={restaurantId} value={restaurantId}>
                          {restaurant.name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategori *
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    disabled={!formData.restaurantId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {!formData.restaurantId ? 'Önce restoran seçiniz' : 'Kategori seçiniz'}
                    </option>
                    {getFilteredCategories().map((category) => {
                      const categoryId = getCategoryId(category);
                      return (
                        <option key={categoryId} value={categoryId}>
                          {category.name}
                        </option>
                      );
                    })}
                  </select>
                  {formData.restaurantId && getFilteredCategories().length === 0 && (
                    <p className="text-sm text-amber-600 mt-1">
                      Bu restoran için kategori bulunamadı. Önce kategori oluşturun.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Adı *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ürün adını girin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İngilizce Ürün Adı (Opsiyonel)
                  </label>
                  <input
                    type="text"
                    name="nameEn"
                    value={formData.nameEn}
                    onChange={handleInputChange}
                    placeholder="English product name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Ürün açıklaması"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fiyat *
                    </label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="12,50 veya 12.50"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      İndirimli Fiyat
                    </label>
                    <input
                      type="text"
                      name="discountedPrice"
                      value={formData.discountedPrice}
                      onChange={handleInputChange}
                      placeholder="10,00 veya 10.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Details & Features */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-900">Detaylar ve Özellikler</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Resmi
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {formData.image && (
                      <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="font-medium text-blue-800">📸 Seçilen resim: {formData.originalFileName}</p>
                        <p className="text-blue-700">💾 Kaydedilecek dosya adı: <code className="bg-blue-100 px-1 rounded">{formData.image.split('/').pop()}</code></p>
                        
                         
                       
                      </div>
                    )}
                    {formData.imagePreview && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700 mb-2">Önizleme:</p>
                        <img 
                          src={formData.imagePreview} 
                          alt="Resim önizleme" 
                          className="w-32 h-24 object-cover rounded-lg border border-gray-300"
                        />
                      </div>
                    )}
                    

                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İçerikler
                  </label>
                  <textarea
                    name="ingredients"
                    value={formData.ingredients}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Dana eti, domates, marul, soğan..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kalori
                    </label>
                    <input
                      type="number"
                      name="calories"
                      value={formData.calories}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="350"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hazırlık Süresi (dk)
                    </label>
                    <input
                      type="number"
                      name="preparationTime"
                      value={formData.preparationTime}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="15"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sıralama
                  </label>
                  <input
                    type="number"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Dietary Options */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Diyet Seçenekleri</h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isVegetarian"
                        name="isVegetarian"
                        checked={formData.isVegetarian}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isVegetarian" className="text-sm text-gray-700">
                        Vejetaryen
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isVegan"
                        name="isVegan"
                        checked={formData.isVegan}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isVegan" className="text-sm text-gray-700">
                        Vegan
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isGlutenFree"
                        name="isGlutenFree"
                        checked={formData.isGlutenFree}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isGlutenFree" className="text-sm text-gray-700">
                        Glütensiz
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isSpicy"
                        name="isSpicy"
                        checked={formData.isSpicy}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isSpicy" className="text-sm text-gray-700">
                        Acılı
                      </label>
                    </div>
                  </div>

                  {formData.isSpicy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Acılık Seviyesi (1-5)
                      </label>
                      <input
                        type="number"
                        name="spicyLevel"
                        value={formData.spicyLevel}
                        onChange={handleInputChange}
                        min="1"
                        max="5"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>

                {/* Status Options */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Durum</h4>
                  
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isAvailable"
                        name="isAvailable"
                        checked={formData.isAvailable}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isAvailable" className="text-sm text-gray-700">
                        Mevcut
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isFeatured"
                        name="isFeatured"
                        checked={formData.isFeatured}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isFeatured" className="text-sm text-gray-700">
                        Öne Çıkan
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        {/* Products List */}
        <div className="p-6">
          {/* Search Bar */}
          {products.length > 0 && (
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Ürün adına göre ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {searchTerm && (
                <div className="mt-2 text-sm text-gray-600">
                  {getFilteredProducts().length} ürün bulundu
                </div>
              )}
            </div>
          )}

          {loading && products.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Yükleniyor...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz ürün eklenmemiş</h3>
              <p className="text-gray-600 mb-4">İlk ürününüzü ekleyerek başlayın</p>
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ürün Ekle
              </button>
            </div>
          ) : (
            <>
              {getFilteredProducts().length === 0 && searchTerm ? (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Arama sonucu bulunamadı</h3>
                  <p className="text-gray-600 mb-4">"{searchTerm}" için ürün bulunamadı</p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Aramayı Temizle
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getFilteredProducts().map((product) => (
                <div key={product.productId || product.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Product Image */}
                  <div className="relative h-48 bg-gray-200">
                    {getProductImageUrl(product.image) ? (
                                              <img 
                        src={getProductImageUrl(product.image)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <ShoppingBag className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                      {product.isFeatured && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Star className="h-3 w-3 mr-1" />
                          Öne Çıkan
                        </span>
                      )}
                      {product.isVegetarian && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <Leaf className="h-3 w-3 mr-1" />
                          Vejetaryen
                        </span>
                      )}
                      {product.isSpicy && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <Flame className="h-3 w-3 mr-1" />
                          Acılı
                        </span>
                      )}
                    </div>

                    {/* Status Toggle */}
                    <div className="absolute top-2 right-2 z-10">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Buton tıklandı, ürün:', product);
                          toggleProductStatus(product);
                        }}
                        className={`p-1 rounded-full ${product.isAvailable ? 'bg-green-500' : 'bg-gray-500'} text-white hover:opacity-80 transition-opacity cursor-pointer`}
                        type="button"
                      >
                        {product.isAvailable ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{product.name}</h3>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            product.isAvailable 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {product.isAvailable ? 'Mevcut' : 'Mevcut Değil'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            const possibleIds = [product.productId, product.id, product.ProductId, product.ID];
                            const actualId = possibleIds.find(id => id != null);
                            if (actualId) {
                              handleDelete(actualId);
                            } else {
                              setError('Product ID bulunamadı');
                            }
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {product.description && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                    )}

                    {/* Price */}
                    <div className="mb-3">
                      {product.discountedPrice ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-green-600">₺{product.discountedPrice}</span>
                          <span className="text-sm text-gray-500 line-through">₺{product.price}</span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">₺{product.price}</span>
                      )}
                    </div>

                    {/* Additional Info */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      {product.preparationTime && (
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>{product.preparationTime} dk</span>
                        </div>
                      )}
                      {product.calories && (
                        <span>{product.calories} kcal</span>
                      )}
                    </div>

                    {/* Dietary Icons */}
                    <div className="flex items-center space-x-2 mb-3">
                      {product.isVegan && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <Leaf className="h-4 w-4" />
                          <span className="text-xs">Vegan</span>
                        </div>
                      )}
                      {product.isGlutenFree && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Glütensiz
                        </span>
                      )}
                      {product.isSpicy && product.spicyLevel > 0 && (
                        <div className="flex items-center space-x-1 text-red-600">
                          <Flame className="h-4 w-4" />
                          <span className="text-xs">{product.spicyLevel}/5</span>
                        </div>
                      )}
                    </div>

                    <div className="text-xs text-gray-500 space-y-1">
                      <div>Restoran: {getRestaurantName(product.restaurantId)}</div>
                      <div>Kategori: {getCategoryName(product.categoryId)}</div>
                      {product.createdDate && (
                        <div>Oluşturulma: {formatDate(product.createdDate)}</div>
                      )}
                    </div>

                    {product.ingredients && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                          <span className="font-medium">İçerikler:</span> {product.ingredients}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsManagement;